import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { CheckCircle2, Users, ChevronLeft, ChevronRight, DollarSign, Layers, Crosshair } from 'lucide-react';
import { Button } from '../components/ui/button';
import { parseLocalDate, formatDate } from '../utils/dateUtils';

const API = process.env.REACT_APP_BACKEND_URL;

const Closings = () => {
  const [loans, setLoans] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [isLoaded, setIsLoaded] = useState(false);
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [leadSourceFilter, setLeadSourceFilter] = useState('');

  useEffect(() => {
    const fetchClosings = async () => {
      try {
        const response = await axios.get(`${API}/api/closings`);
        const transformedLoans = response.data.map(loan => ({
          id: loan.id,
          userName: loan.user_name,
          clientName: loan.client_name,
          loanNumber: loan.loan_number,
          loanAmount: loan.loan_amount,
          lender: loan.lender,
          processor: loan.processor,
          state: loan.state,
          loanType: loan.loan_type,
          leadSource: loan.lead_source,
          date: loan.date,
          fundingDate: loan.funding_date,
        }));
        setLoans(transformedLoans);
        setIsLoaded(true);
      } catch (error) {
        console.error('Error fetching closings:', error);
        setIsLoaded(true);
      }
    };
    fetchClosings();
  }, []);

  // Filter loans by CLOSING date (loan.date)
  const getMonthLoans = () => {
    return loans.filter(loan => {
      if (!loan.date) return false;
      const closingDate = parseLocalDate(loan.date);
      return (
        closingDate.getMonth() === selectedMonth.getMonth() &&
        closingDate.getFullYear() === selectedMonth.getFullYear()
      );
    });
  };

  const monthLoans = getMonthLoans();

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortedMonthLoans = [...monthLoans]
    .filter(loan => !leadSourceFilter || loan.leadSource === leadSourceFilter)
    .sort((a, b) => {
    if (!sortField) return 0;
    const dir = sortDir === 'asc' ? 1 : -1;
    if (sortField === 'broker') {
      return dir * (a.userName || '').localeCompare(b.userName || '');
    }
    if (sortField === 'closingDate') {
      return dir * ((a.date || '').localeCompare(b.date || ''));
    }
    return 0;
  });

  const totalLoans = monthLoans.length;
  const totalVolume = monthLoans.reduce((sum, loan) => sum + loan.loanAmount, 0);

  // Group by broker
  const brokerStats = monthLoans.reduce((acc, loan) => {
    const broker = loan.userName || 'Unknown';
    if (!acc[broker]) acc[broker] = { volume: 0, count: 0 };
    acc[broker].volume += loan.loanAmount;
    acc[broker].count += 1;
    return acc;
  }, {});

  // Group by loan type
  const loanTypeStats = monthLoans.reduce((acc, loan) => {
    const loanType = loan.loanType || 'Unknown';
    if (!acc[loanType]) acc[loanType] = { count: 0, volume: 0 };
    acc[loanType].count += 1;
    acc[loanType].volume += loan.loanAmount;
    return acc;
  }, {});

  // Group by lead source
  const leadSourceStats = monthLoans.reduce((acc, loan) => {
    const source = loan.leadSource || 'Unknown';
    if (!acc[source]) acc[source] = { count: 0, volume: 0 };
    acc[source].count += 1;
    acc[source].volume += loan.loanAmount;
    return acc;
  }, {});

  const allLoanTypes = [
    'Cash Out Refinance', 'Rate & Term', 'Purchase', 'Fixed 2nd', 'HELOC',
    'Construction Purchase', 'Construction Refinance', 'DSCR Purchase',
    'DSCR Refinance', 'IRRRL', 'FHA Streamline'
  ];

  const sortedBrokers = Object.entries(brokerStats).sort((a, b) => b[1].volume - a[1].volume);

  const formatMonth = (date) => date.toLocaleString('default', { month: 'long', year: 'numeric' });

  const navigateMonth = (direction) => {
    const newDate = new Date(selectedMonth);
    newDate.setMonth(newDate.getMonth() + direction);
    setSelectedMonth(newDate);
  };

  const formatVolume = (volume) => {
    if (volume >= 1000000) return `$${(volume / 1000000).toFixed(1)}M`;
    return `$${(volume / 1000).toFixed(0)}k`;
  };

  if (!isLoaded) {
    return (
      <div className="min-h-screen bg-black p-6 flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black p-4 lg:p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl lg:text-3xl font-semibold text-white" data-testid="closings-title">Kinfolk Closings</h1>
        <p className="text-gray-400 text-sm mt-1">Track all loans closed by closing date</p>
      </div>

      {/* Month Navigation + Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-[#111] border-[#222] overflow-hidden">
          <CardContent className="p-5">
            <p className="text-gray-400 text-xs uppercase tracking-wide">Month</p>
            <div className="flex items-center gap-2 mt-3">
              <Button variant="ghost" size="icon" onClick={() => navigateMonth(-1)} data-testid="closings-prev-month" className="text-gray-400 hover:text-white hover:bg-[#222] h-6 w-6 flex-shrink-0">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-white font-semibold text-lg flex-1 text-center" data-testid="closings-month">{formatMonth(selectedMonth)}</span>
              <Button variant="ghost" size="icon" onClick={() => navigateMonth(1)} data-testid="closings-next-month" className="text-gray-400 hover:text-white hover:bg-[#222] h-6 w-6 flex-shrink-0">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#111] border-[#222] border-l-4 border-l-emerald-500 overflow-hidden">
          <CardContent className="p-5">
            <p className="text-gray-400 text-xs uppercase tracking-wide">Total Volume</p>
            <p className="text-emerald-400 font-bold text-xl mt-3" data-testid="closings-volume">${totalVolume.toLocaleString()}</p>
          </CardContent>
        </Card>

        <Card className="bg-[#111] border-[#222] border-l-4 border-l-blue-500 overflow-hidden">
          <CardContent className="p-5">
            <p className="text-gray-400 text-xs uppercase tracking-wide">Closings</p>
            <p className="text-blue-400 font-bold text-xl mt-3" data-testid="closings-count">{totalLoans}</p>
          </CardContent>
        </Card>

        <Card className="bg-[#111] border-[#222] border-l-4 border-l-purple-500 overflow-hidden">
          <CardContent className="p-5">
            <p className="text-gray-400 text-xs uppercase tracking-wide">Active Brokers</p>
            <p className="text-purple-400 font-bold text-xl mt-3">{Object.keys(brokerStats).length}</p>
          </CardContent>
        </Card>
      </div>

      {/* Closings Leaderboard */}
      {sortedBrokers.length > 0 && (
        <Card className="bg-gradient-to-br from-[#111] to-[#0a0a0a] border-[#222]">
          <CardHeader className="pb-4">
            <CardTitle className="text-white text-lg flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-lg flex items-center justify-center">
                <Users className="w-4 h-4 text-white" />
              </div>
              Closings Leaderboard
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {/* Top 3 Podium */}
            <div className="flex justify-center items-end gap-5 mb-8 pt-10">
              {sortedBrokers[1] && (
                <div className="w-44 bg-gradient-to-b from-[#3a3a3a] to-[#1a1a1a] border-2 border-gray-500 rounded-2xl p-4 text-center shadow-xl shadow-gray-900/50 relative" data-testid="closings-podium-2nd">
                  <div className="absolute -top-5 left-1/2 -translate-x-1/2 w-11 h-11 bg-gradient-to-br from-gray-300 to-gray-500 rounded-full flex items-center justify-center shadow-lg border-4 border-[#111]">
                    <span className="text-gray-900 font-black text-lg">2</span>
                  </div>
                  <div className="mt-4">
                    <p className="text-white font-bold text-base mb-1">{sortedBrokers[1][0]}</p>
                    <p className="text-green-400 font-black text-xl">{formatVolume(sortedBrokers[1][1].volume)}</p>
                    <p className="text-gray-400 text-xs mt-1">{sortedBrokers[1][1].count} loan{sortedBrokers[1][1].count !== 1 ? 's' : ''}</p>
                  </div>
                </div>
              )}
              {sortedBrokers[0] && (
                <div className="w-72 bg-gradient-to-b from-emerald-500/30 to-emerald-900/20 border-2 border-emerald-500 rounded-2xl p-8 text-center shadow-2xl shadow-emerald-500/30 relative -mb-2" data-testid="closings-podium-1st">
                  <div className="absolute -top-7 left-1/2 -translate-x-1/2 w-16 h-16 bg-gradient-to-br from-emerald-300 to-emerald-500 rounded-full flex items-center justify-center shadow-xl shadow-emerald-500/50 border-4 border-[#111]">
                    <span className="text-emerald-900 font-black text-3xl">1</span>
                  </div>
                  <div className="mt-6">
                    <p className="text-white font-bold text-2xl mb-2">{sortedBrokers[0][0]}</p>
                    <p className="text-green-400 font-black text-4xl">{formatVolume(sortedBrokers[0][1].volume)}</p>
                    <p className="text-gray-300 text-sm mt-2">{sortedBrokers[0][1].count} loan{sortedBrokers[0][1].count !== 1 ? 's' : ''}</p>
                  </div>
                </div>
              )}
              {sortedBrokers[2] && (
                <div className="w-44 bg-gradient-to-b from-orange-800/30 to-[#1a1a1a] border-2 border-orange-600 rounded-2xl p-4 text-center shadow-xl shadow-orange-900/30 relative" data-testid="closings-podium-3rd">
                  <div className="absolute -top-5 left-1/2 -translate-x-1/2 w-11 h-11 bg-gradient-to-br from-orange-400 to-orange-600 rounded-full flex items-center justify-center shadow-lg border-4 border-[#111]">
                    <span className="text-white font-black text-lg">3</span>
                  </div>
                  <div className="mt-4">
                    <p className="text-white font-bold text-base mb-1">{sortedBrokers[2][0]}</p>
                    <p className="text-green-400 font-black text-xl">{formatVolume(sortedBrokers[2][1].volume)}</p>
                    <p className="text-gray-400 text-xs mt-1">{sortedBrokers[2][1].count} loan{sortedBrokers[2][1].count !== 1 ? 's' : ''}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Rest of Brokers */}
            {sortedBrokers.length > 3 && (
              <div className="border-t border-[#222] pt-4">
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  {sortedBrokers.slice(3).map(([broker, stats], index) => (
                    <div key={broker} className="bg-[#0d0d0d] border border-[#222] rounded-xl p-4 hover:border-[#333] transition-colors">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="bg-[#222] text-gray-400 text-xs font-bold px-2 py-1 rounded">#{index + 4}</span>
                      </div>
                      <p className="text-white font-medium text-sm mb-2">{broker}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-green-400 font-bold">{formatVolume(stats.volume)}</span>
                        <span className="text-gray-400 text-sm">{stats.count} loan{stats.count !== 1 ? 's' : ''}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
        {/* Closings Table */}
        <div className="xl:col-span-3">
          <Card className="bg-[#111] border-[#222]">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                  Closed Loans
                  {leadSourceFilter && <span className="text-sm text-amber-400 font-normal ml-2">({leadSourceFilter})</span>}
                </CardTitle>
                <select
                  value={leadSourceFilter}
                  onChange={(e) => setLeadSourceFilter(e.target.value)}
                  className="px-3 py-1.5 bg-[#1a1a1a] border border-[#333] rounded-md text-sm text-white focus:border-green-500 focus:outline-none"
                  data-testid="closings-lead-source-filter"
                >
                  <option value="">All Lead Sources</option>
                  {[...new Set(monthLoans.map(l => l.leadSource).filter(Boolean))].sort().map(ls => (
                    <option key={ls} value={ls}>{ls}</option>
                  ))}
                </select>
              </div>
            </CardHeader>
            <CardContent>
              {monthLoans.length === 0 ? (
                <div className="text-center py-12">
                  <DollarSign className="w-12 h-12 text-gray-700 mx-auto mb-3" />
                  <p className="text-gray-500">No closings for this month</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="closings-table">
                    <thead>
                      <tr className="border-b border-[#333]">
                        <th className="text-left py-3 px-2 text-gray-400 font-medium cursor-pointer select-none hover:text-white transition-colors" onClick={() => toggleSort('broker')}>
                          Broker {sortField === 'broker' ? (sortDir === 'asc' ? '↑' : '↓') : ''}
                        </th>
                        <th className="text-left py-3 px-2 text-gray-400 font-medium">Client</th>
                        <th className="text-left py-3 px-2 text-gray-400 font-medium">Loan #</th>
                        <th className="text-left py-3 px-2 text-gray-400 font-medium">Lender</th>
                        <th className="text-left py-3 px-2 text-gray-400 font-medium">Lead Source</th>
                        <th className="text-right py-3 px-2 text-gray-400 font-medium">Amount</th>
                        <th className="text-left py-3 px-2 text-gray-400 font-medium">Processor</th>
                        <th className="text-center py-3 px-2 text-gray-400 font-medium cursor-pointer select-none hover:text-white transition-colors" onClick={() => toggleSort('closingDate')}>
                          Closing Date {sortField === 'closingDate' ? (sortDir === 'asc' ? '↑' : '↓') : ''}
                        </th>
                        <th className="text-center py-3 px-2 text-gray-400 font-medium">Funding Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedMonthLoans.map((loan, index) => (
                        <tr key={loan.id} className={`border-b border-[#222] hover:bg-[#1a1a1a] transition-colors ${index % 2 === 0 ? 'bg-[#0d0d0d]' : ''}`}>
                          <td className="py-3 px-2 text-white font-medium">{loan.userName}</td>
                          <td className="py-3 px-2 text-white">{loan.clientName}</td>
                          <td className="py-3 px-2 text-gray-300">{loan.loanNumber}</td>
                          <td className="py-3 px-2 text-gray-300">{loan.lender || '—'}</td>
                          <td className="py-3 px-2 text-gray-300">{loan.leadSource || '—'}</td>
                          <td className="py-3 px-2 text-white text-right">${loan.loanAmount.toLocaleString()}</td>
                          <td className="py-3 px-2 text-gray-300">{loan.processor || '—'}</td>
                          <td className="py-3 px-2 text-center text-gray-300">
                            {formatDate(loan.date)}
                          </td>
                          <td className="py-3 px-2 text-center">
                            {loan.fundingDate ? (
                              <Badge className="bg-green-900/30 text-green-400 border-green-800 text-xs">
                                {formatDate(loan.fundingDate)}
                              </Badge>
                            ) : (
                              <span className="text-gray-500">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-[#333] bg-[#1a1a1a]">
                        <td colSpan="4" className="py-3 px-2 text-white font-semibold">Total ({totalLoans} loan{totalLoans !== 1 ? 's' : ''})</td>
                        <td className="py-3 px-2 text-white text-right font-semibold">${totalVolume.toLocaleString()}</td>
                        <td colSpan="3"></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Loan Type & Lead Source Breakdown */}
        <div className="space-y-6">
          <Card className="bg-[#111] border-[#222]">
            <CardHeader className="pb-3">
              <CardTitle className="text-white text-base flex items-center gap-2">
                <Layers className="w-4 h-4 text-purple-400" />
                Loan Type
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-1">
                {allLoanTypes.map((loanType) => {
                  const stats = loanTypeStats[loanType] || { count: 0, volume: 0 };
                  if (stats.count === 0) return null;
                  return (
                    <div key={loanType} className="flex items-center justify-between py-2 border-b border-[#222] last:border-0">
                      <span className="text-white text-sm">{loanType}</span>
                      <div className="flex items-center gap-3">
                        <span className="text-gray-400 text-sm">{stats.count}</span>
                        <span className="text-green-400 text-sm font-medium">{formatVolume(stats.volume)}</span>
                      </div>
                    </div>
                  );
                })}
                {Object.keys(loanTypeStats).filter(k => k !== 'Unknown').length === 0 && (
                  <p className="text-gray-500 text-sm text-center py-4">No loan types recorded</p>
                )}
                <div className="flex items-center justify-between py-3 mt-2 border-t border-[#333]">
                  <span className="text-white text-sm font-semibold">Total</span>
                  <div className="flex items-center gap-3">
                    <span className="text-white text-sm font-semibold">{totalLoans}</span>
                    <span className="text-green-400 text-sm font-semibold">{formatVolume(totalVolume)}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Lead Source Breakdown */}
          <Card className="bg-[#111] border-[#222]">
            <CardHeader className="pb-3">
              <CardTitle className="text-white text-base flex items-center gap-2">
                <Crosshair className="w-4 h-4 text-cyan-400" />
                Lead Source
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-1">
                {Object.entries(leadSourceStats)
                  .filter(([k]) => k !== 'Unknown')
                  .sort((a, b) => b[1].volume - a[1].volume)
                  .map(([source, stats]) => (
                    <div key={source} className="flex items-center justify-between py-2 border-b border-[#222] last:border-0">
                      <span className="text-white text-sm">{source}</span>
                      <div className="flex items-center gap-3">
                        <span className="text-gray-400 text-sm">{stats.count}</span>
                        <span className="text-green-400 text-sm font-medium">{formatVolume(stats.volume)}</span>
                      </div>
                    </div>
                  ))}
                {Object.keys(leadSourceStats).filter(k => k !== 'Unknown').length === 0 && (
                  <p className="text-gray-500 text-sm text-center py-4">No lead sources recorded</p>
                )}
                <div className="flex items-center justify-between py-3 mt-2 border-t border-[#333]">
                  <span className="text-white text-sm font-semibold">Total</span>
                  <div className="flex items-center gap-3">
                    <span className="text-white text-sm font-semibold">{totalLoans}</span>
                    <span className="text-green-400 text-sm font-semibold">{formatVolume(totalVolume)}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Closings;
